import { $api } from './api';
import { $http } from './http';
import { YId } from './types';

interface IDModel<IdType> { id: IdType };

interface DataStore<Model, IdType, Object> {
    all: Object[];
    init(models: Model[]): Object[];
    register(model: Model): Object;
    getAll(reload?: boolean): Promise<Object[]>;
    get(id: IdType): Promise<Object>;
    maybe(id: IdType): Object | undefined;
    update(id: IdType, params: any): Promise<Object>;
    insert(params: any): Promise<Object>;
    upsert(id: IdType | undefined, params: any): Promise<Object>;
    remove(id: IdType): Promise<void>;
}

export function makeDataStore<Model extends IDModel<IdType>, IdType extends YId = Model['id'], Object = Model>(all_path: string, one_path: string, mapper: (m: Model) => Object,
    updater: (o: Object, m: Model) => void): DataStore<Model, IdType, Object> {
    let all_promise: Promise<Object[]> | undefined;
    const all: Object[] = [];
    let all_by_id: { [K in IdType]?: Object } = {};

    function registerAll(models: Model[]) {
        all.length = 0;
        all_by_id = {};
        for(const m of models) {
            const id = m.id;
            const obj = mapper(m);
            all.push(obj);
            all_by_id[id] = obj;
        }

        if(!all_promise)
            all_promise = Promise.resolve(all);

        return all;
    }

    function register(m: Model) {
        const id = m.id;
        let obj = all_by_id[id];
        if(obj) {
            updater(obj, m);
        } else {
            obj = mapper(m);
            all.push(obj!);
            all_by_id[id] = obj;
        }

        return obj!;
    }

    return {
        all: all,
        init: registerAll,
        register: register,
        getAll: (reload?: boolean) => {
            if(!all_promise || reload) {
                all_promise = $http.GET<Model[]>('/api/v1/' + all_path).then(models => {
                    return registerAll(models);
                }, $api.errorHandler);
            }

            return all_promise;
        },
        get: (id: IdType) => {
            const obj = all_by_id[id];
            if(obj)
                return Promise.resolve(obj);

            return $http.GET<Model>('/api/v1/' + one_path + '/' + id).then(model => {
                return register(model);
            }, $api.errorHandler);
        },
        maybe: (id: IdType) => all_by_id[id],
        update: (id: IdType, params: any) => {
            return $http.PUT<Model>('/api/v1/' + one_path + '/' + id, params).then(model => {
                return register(model);
            }, $api.errorHandler);
        },
        insert: (params: any) => {
            return $http.POST<Model>('/api/v1/' + one_path, params).then(model => {
                return register(model);
            }, $api.errorHandler);
        },
        upsert: (id: IdType | undefined, params: any) => {
            if(id != null) {
                return $http.PUT<Model>('/api/v1/' + one_path + '/' + id, params).then(model => {
                    return register(model);
                }, $api.errorHandler);
            } else {
                return $http.POST<Model>('/api/v1/' + one_path, params).then(model => {
                    return register(model);
                }, $api.errorHandler);
            }
        },
        remove: (id: IdType) => {
            return $http.DELETE('/api/v1/' + one_path + '/' + id).then(() => {
                const obj = all_by_id[id];
                if(obj) {
                    const index = all.indexOf(obj);
                    if(index >= 0)
                        all.splice(index, 1);
                }
            }, $api.errorHandler);
        }
    }
}
